import { Controller } from "@hotwired/stimulus"

// Navbar "Чат" button and mobile tapbar toggle — both re-rendered on every
// navigation, while the docked chat panel is a permanent node
// (chat_controller.js). Per D6, they only talk via document CustomEvents:
// this dispatches "chat:toggle" to open/close the panel, and listens for
// "chat:unread" (dispatched by chat_controller.js) to keep the badge live.
// The server-rendered initial count always wins on the next navigation.
//
// Active-state (both instances): the panel is
// `data-turbo-permanent`, so its connect() does not re-fire on a same-class
// morph and cannot reliably re-emit "chat:state" to a freshly rendered
// toggle. Self-correct is therefore driven by THIS controller: connect()
// reads the panel's live DOM directly to seed the initial active state, and
// the "chat:state" listener (dispatched by chat_controller.js) keeps it in
// sync afterwards. On a full-page, cross-URL, same-class visit (non-morph),
// Turbo's Bardo permanent-element handling swaps in a placeholder for the
// panel before the new body is attached and only re-splices the real node
// back in later, so the connect-time read can race it; a "turbo:load"
// re-sync (fired after Bardo restoration completes) covers that case.
//
// The state is published as `aria-pressed` on the controller element. The
// navbar pill styles itself from that attribute (`aria-pressed:` variants in
// shared/_chat_button); the tapbar toggle additionally carries the "active"
// target, which gets the BottomNav link's class treatment.
export default class extends Controller {
  static targets = ["badge", "count", "active"]

  static values = {
    count: Number,
    ariaTemplateOne: String,
    ariaTemplateOther: String,
  }

  connect() {
    this.boundHandleUnread = this.handleUnread.bind(this)
    document.addEventListener("chat:unread", this.boundHandleUnread)

    this.boundHandleState = this.handleState.bind(this)
    document.addEventListener("chat:state", this.boundHandleState)

    this.syncActiveFromPanel()

    this.boundSyncActiveFromPanel = this.syncActiveFromPanel.bind(this)
    document.addEventListener("turbo:load", this.boundSyncActiveFromPanel)
    // "turbo:load" only fires once the visit's view transition is over, many
    // frames after the new body is on screen. "turbo:render" fires as soon as
    // Bardo has spliced the panel back in -- before the first paint -- so the
    // toggle never shows a stale inactive frame on a cross-URL visit.
    document.addEventListener("turbo:render", this.boundSyncActiveFromPanel)
    // Same-URL morph refresh: idiomorph patches this non-permanent toggle
    // back to the server-rendered inactive state without disconnecting the
    // controller. "turbo:load" follows, but only after a visibly inactive
    // frame -- re-sync in the morph tick itself.
    document.addEventListener("turbo:morph", this.boundSyncActiveFromPanel)
  }

  disconnect() {
    document.removeEventListener("chat:unread", this.boundHandleUnread)
    document.removeEventListener("chat:state", this.boundHandleState)
    document.removeEventListener("turbo:load", this.boundSyncActiveFromPanel)
    document.removeEventListener("turbo:render", this.boundSyncActiveFromPanel)
    document.removeEventListener("turbo:morph", this.boundSyncActiveFromPanel)
  }

  toggle() {
    document.dispatchEvent(new CustomEvent("chat:toggle"))
  }

  handleUnread(event) {
    if (event.detail?.reset) {
      this.countValue = 0
    } else if (event.detail?.delta) {
      this.countValue += event.detail.delta
    }
  }

  handleState(event) {
    this.setActive(!!event.detail?.open)
  }

  syncActiveFromPanel() {
    this.setActive(!!document.querySelector('[data-chat-target="panel"]:not(.hidden)'))
  }

  // Mirrors Ui::Layout::BottomNav::Link::Component#link_classes' `active:`
  // treatment (text-brand-600 vs text-content-muted) so the JS active state
  // is visually consistent with a server-rendered `active: true` link.
  setActive(isActive) {
    this.element.setAttribute("aria-pressed", String(isActive))
    if (!this.hasActiveTarget) return

    this.activeTarget.classList.toggle("text-brand-600", isActive)
    this.activeTarget.classList.toggle("text-content-muted", !isActive)
  }

  countValueChanged() {
    if (!this.hasBadgeTarget) return

    this.badgeTarget.classList.toggle("hidden", this.countValue <= 0)
    if (this.hasCountTarget) this.countTarget.textContent = this.countValue
    const ariaTemplate = this.countValue === 1 ? this.ariaTemplateOneValue : this.ariaTemplateOtherValue
    if (ariaTemplate) {
      this.badgeTarget.setAttribute("aria-label", ariaTemplate.replace("__COUNT__", this.countValue))
    }
  }
}
